import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router";
import {
  customersFromCsv,
  formatCents,
  KIND_LABEL,
  ROLE_LABEL,
  ROLE_PLURAL,
  CUSTOMER_ROLES,
  type BillingSettings,
  type CsvImportResult,
  type CustomerDetail,
  type CustomerInfo,
  type CustomerKind,
  type CustomerRole,
  type CustomerSuggestion,
  type ItemInfo,
  type MeResponse,
} from "@handvero/shared";
import { api, errorText } from "../api";
import { Icon, Message, Sheet, useUnauthorizedRedirect } from "./ui";
import { BrandingSheet, BrandStrip, useBranding } from "./ProtocolSharing";
import "./customers.css";

/*
 * Kunden, Leistungen und Firmenangaben: die Grundlage für Angebote, Aufträge und Rechnungen.
 * Dieselben Kunden gelten für die Projekte. Nur am PC (/desktop), freigeschaltet pro Firma.
 */

type Tab = "kunden" | "leistungen";

export function CustomersView({ me }: { me: MeResponse }) {
  const [tab, setTab] = useState<Tab>("kunden");
  const manager = me.user.role !== "employee";
  const tabs: [Tab, string][] = [
    ["kunden", "Kunden"],
    ["leistungen", "Leistungen und Artikel"],
  ];
  return (
    <div className="cu">
      <nav className="cu-tabs" aria-label="Bereiche">
        {tabs.map(([id, label]) => (
          <button key={id} className={tab === id ? "on" : undefined} onClick={() => setTab(id)}>
            {label}
          </button>
        ))}
      </nav>
      {tab === "kunden" && <CustomerList manager={manager} />}
      {tab === "leistungen" && <ItemList manager={manager} />}
    </div>
  );
}

// ---------- Kunden ----------

function CustomerList({ manager }: { manager: boolean }) {
  const onAuth = useUnauthorizedRedirect();
  const [role, setRole] = useState<CustomerRole | "all">("all");
  const [query, setQuery] = useState("");
  const [archived, setArchived] = useState(false);
  const [list, setList] = useState<CustomerInfo[] | null>(null);
  const [error, setError] = useState("");
  const [edit, setEdit] = useState<CustomerInfo | "new" | null>(null);
  const [importing, setImporting] = useState(false);
  const [suggestions, setSuggestions] = useState<CustomerSuggestion[]>([]);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (role !== "all") params.set("role", role);
    if (query.trim()) params.set("q", query.trim());
    if (archived) params.set("archived", "1");
    api
      .get<CustomerInfo[]>(`/api/customers?${params}`)
      .then((l) => {
        setList(l);
        setError("");
      })
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [role, query, archived, onAuth]);

  // Beim Tippen kurz warten, damit nicht jede Taste eine Abfrage auslöst.
  useEffect(() => {
    const timer = setTimeout(load, query ? 250 : 0);
    return () => clearTimeout(timer);
  }, [load, query]);

  useEffect(() => {
    if (!manager) return;
    api.get<CustomerSuggestion[]>("/api/customers/vorschlaege").then(setSuggestions).catch(() => undefined);
  }, [manager, list]);

  return (
    <section className="cu-panel">
      <div className="cu-bar">
        <div className="cu-roles" role="tablist">
          {(["all", ...CUSTOMER_ROLES] as const).map((r) => (
            <button key={r} role="tab" aria-selected={role === r} className={role === r ? "on" : undefined} onClick={() => setRole(r)}>
              {r === "all" ? "Alle" : ROLE_PLURAL[r]}
            </button>
          ))}
        </div>
        <label className="dsearch cu-search">
          <Icon name="search" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Name, Ort, Nummer …" aria-label="Kunden suchen" />
        </label>
        <div className="cu-actions">
          {manager && (
            <button className="dbtn ghost" onClick={() => setImporting(true)}>
              <Icon name="download" /> Importieren
            </button>
          )}
          <button className="dbtn" onClick={() => setEdit("new")}>
            <Icon name="plus" /> Kontakt erstellen
          </button>
        </div>
      </div>

      {manager && suggestions.length > 0 && !archived && (
        <div className="cu-hint">
          <p>
            <b>{suggestions.length} Kundennamen</b> stehen schon in Ihren Projekten. Mit einem Klick werden daraus Kunden. Nichts wird gelöscht oder
            verändert.
          </p>
          <button className="dbtn small" onClick={() => setImporting(true)}>
            Aus Projekten übernehmen
          </button>
        </div>
      )}

      <Message>{error}</Message>
      {list === null && !error && <p className="muted-s">Lädt …</p>}
      {list?.length === 0 && (
        <p className="cu-empty">
          {archived ? "Keine archivierten Kontakte." : query || role !== "all" ? "Dazu gibt es keinen Eintrag." : "Noch keine Kunden. Legen Sie den ersten an oder übernehmen Sie Ihre bisherigen."}
        </p>
      )}
      {!!list?.length && (
        <table className="cu-table">
          <thead>
            <tr>
              <th>Art</th>
              <th>Kd.-Nr.</th>
              <th>Name</th>
              <th>Ort</th>
              <th>Stichworte</th>
              <th className="r">Projekte</th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} onClick={() => setEdit(c)} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setEdit(c)}>
                <td>
                  <span className={`cu-kind ${c.kind}`} title={KIND_LABEL[c.kind]}>
                    <Icon name={c.kind === "company" ? "folder" : "user"} />
                  </span>
                  {c.role !== "customer" && <small className="cu-role">{ROLE_LABEL[c.role]}</small>}
                </td>
                <td className="mono">{c.number}</td>
                <td>
                  <b>{c.name}</b>
                  {c.contactName && <small>{c.contactName}</small>}
                </td>
                <td>{[c.postalCode, c.city].filter(Boolean).join(" ") || "–"}</td>
                <td>
                  {c.tags.map((t) => (
                    <span key={t} className="cu-tag">
                      {t}
                    </span>
                  ))}
                </td>
                <td className="r">{c.projectCount || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="cu-foot">
        <button className="cu-link" onClick={() => setArchived((a) => !a)}>
          {archived ? "Zurück zu den aktiven Kontakten" : "Archivierte Kontakte anzeigen"}
        </button>
      </p>

      {edit && (
        <CustomerSheet
          customer={edit === "new" ? null : edit}
          manager={manager}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            load();
          }}
        />
      )}
      {importing && (
        <ImportSheet
          manager={manager}
          suggestions={suggestions}
          onClose={() => setImporting(false)}
          onDone={() => {
            setImporting(false);
            load();
          }}
        />
      )}
    </section>
  );
}

interface Form {
  kind: CustomerKind;
  role: CustomerRole;
  name: string;
  contactName: string;
  email: string;
  phone: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  vatId: string;
  paymentDays: string;
  tags: string;
  note: string;
}

const emptyForm: Form = {
  kind: "company",
  role: "customer",
  name: "",
  contactName: "",
  email: "",
  phone: "",
  street: "",
  postalCode: "",
  city: "",
  country: "DE",
  vatId: "",
  paymentDays: "",
  tags: "",
  note: "",
};

function formOf(c: CustomerInfo | null): Form {
  if (!c) return emptyForm;
  return {
    kind: c.kind,
    role: c.role,
    name: c.name,
    contactName: c.contactName ?? "",
    email: c.email ?? "",
    phone: c.phone ?? "",
    street: c.street ?? "",
    postalCode: c.postalCode ?? "",
    city: c.city ?? "",
    country: c.country,
    vatId: c.vatId ?? "",
    paymentDays: c.paymentDays === null ? "" : String(c.paymentDays),
    tags: c.tags.join(", "),
    note: c.note ?? "",
  };
}

function CustomerSheet(props: { customer: CustomerInfo | null; manager: boolean; onClose: () => void; onSaved: () => void }) {
  const onAuth = useUnauthorizedRedirect();
  const { customer } = props;
  const [f, setF] = useState<Form>(formOf(customer));
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    if (customer) api.get<CustomerDetail>(`/api/customers/${customer.id}`).then(setDetail).catch(() => undefined);
  }, [customer]);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError("");
    try {
      await fn();
      props.onSaved();
    } catch (err) {
      if (!onAuth(err)) setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const body = {
      kind: f.kind,
      role: f.role,
      name: f.name,
      contactName: f.contactName,
      email: f.email,
      phone: f.phone,
      street: f.street,
      postalCode: f.postalCode,
      city: f.city,
      country: f.country || "DE",
      vatId: f.vatId,
      paymentDays: f.paymentDays.trim() === "" ? null : Number(f.paymentDays),
      tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
      note: f.note,
    };
    void run(() => (customer ? api.patch(`/api/customers/${customer.id}`, body) : api.post("/api/customers", body)));
  }

  return (
    <Sheet title={customer ? `Kunde ${customer.number}` : "Kontakt erstellen"} onClose={props.onClose}>
      <form className="cu-form" onSubmit={submit}>
        <div className="cu-seg" role="radiogroup" aria-label="Art">
          {(["company", "person"] as const).map((k) => (
            <button type="button" key={k} role="radio" aria-checked={f.kind === k} className={f.kind === k ? "on" : undefined} onClick={() => set("kind", k)}>
              {KIND_LABEL[k]}
            </button>
          ))}
        </div>
        <label className="wide">
          {f.kind === "company" ? "Firmenname" : "Vor- und Nachname"}
          <input value={f.name} onChange={(e) => set("name", e.target.value)} maxLength={160} required autoFocus />
        </label>
        {f.kind === "company" && (
          <label className="wide">
            Ansprechpartner
            <input value={f.contactName} onChange={(e) => set("contactName", e.target.value)} maxLength={120} />
          </label>
        )}
        <label>
          Kontaktart
          <select value={f.role} onChange={(e) => set("role", e.target.value as CustomerRole)}>
            {CUSTOMER_ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Telefon
          <input value={f.phone} onChange={(e) => set("phone", e.target.value)} maxLength={40} inputMode="tel" />
        </label>
        <label className="wide">
          E-Mail
          <input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} maxLength={254} />
        </label>
        <label className="wide">
          Straße und Hausnummer
          <input value={f.street} onChange={(e) => set("street", e.target.value)} maxLength={120} />
        </label>
        <label>
          PLZ
          <input value={f.postalCode} onChange={(e) => set("postalCode", e.target.value)} maxLength={12} />
        </label>
        <label>
          Ort
          <input value={f.city} onChange={(e) => set("city", e.target.value)} maxLength={80} />
        </label>
        <label>
          Land
          <input value={f.country} onChange={(e) => set("country", e.target.value.toUpperCase())} maxLength={2} placeholder="DE" />
        </label>
        <label>
          Umsatzsteuer-ID
          <input value={f.vatId} onChange={(e) => set("vatId", e.target.value)} maxLength={20} placeholder="DE123456789" />
        </label>
        <label>
          Zahlungsziel (Tage)
          <input value={f.paymentDays} onChange={(e) => set("paymentDays", e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="wie in den Firmenangaben" maxLength={3} />
        </label>
        <label className="wide">
          Stichworte (mit Komma getrennt)
          <input value={f.tags} onChange={(e) => set("tags", e.target.value)} placeholder="z. B. Stammkunde, Neubau" />
        </label>
        <label className="wide">
          Notiz
          <textarea value={f.note} onChange={(e) => set("note", e.target.value)} rows={3} maxLength={2000} />
        </label>

        {detail && detail.projects.length > 0 && (
          <div className="wide cu-projects">
            <b>Projekte dieses Kunden</b>
            <ul>
              {detail.projects.map((p) => (
                <li key={p.id}>
                  <Link to={`/desktop/p/${p.id}`} onClick={props.onClose}>
                    {p.name}
                  </Link>
                  {p.status === "archived" && <small> (archiviert)</small>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <Message>{error}</Message>
        <div className="cu-buttons wide">
          <button className="dbtn" disabled={busy}>
            Speichern
          </button>
          {customer && props.manager && (
            <>
              <button
                type="button"
                className="dbtn ghost"
                disabled={busy}
                onClick={() => void run(() => api.patch(`/api/customers/${customer.id}`, { archived: !customer.archived }))}
              >
                {customer.archived ? "Wiederherstellen" : "Archivieren"}
              </button>
              <button
                type="button"
                className="dbtn ghost danger"
                disabled={busy}
                onClick={() => {
                  if (window.confirm(`„${customer.name}“ wirklich löschen? Kunden mit Projekten lassen sich nur archivieren.`)) {
                    void run(() => api.del(`/api/customers/${customer.id}`));
                  }
                }}
              >
                Löschen
              </button>
            </>
          )}
        </div>
      </form>
    </Sheet>
  );
}

function ImportSheet(props: { manager: boolean; suggestions: CustomerSuggestion[]; onClose: () => void; onDone: () => void }) {
  const onAuth = useUnauthorizedRedirect();
  const [mode, setMode] = useState<"projekte" | "datei">(props.suggestions.length ? "projekte" : "datei");
  const [picked, setPicked] = useState<Set<string>>(() => new Set(props.suggestions.map((s) => s.name)));
  const [csv, setCsv] = useState<CsvImportResult | null>(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState("");

  async function readFile(file: File | undefined) {
    if (!file) return;
    setFileName(file.name);
    setResult("");
    try {
      setCsv(customersFromCsv(await file.text()));
    } catch {
      setError("Die Datei konnte nicht gelesen werden. Bitte speichern Sie sie als CSV (Trennzeichen Semikolon oder Komma).");
    }
  }

  async function run(fn: () => Promise<string>) {
    setBusy(true);
    setError("");
    try {
      setResult(await fn());
    } catch (err) {
      if (!onAuth(err)) setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const fromProjects = () =>
    run(async () => {
      const r = await api.post<{ created: number; linked: number }>("/api/customers/aus-projekten", { names: [...picked] });
      return `${r.created} Kunden angelegt, ${r.linked} Projekte verknüpft.`;
    });

  const fromFile = () =>
    run(async () => {
      const r = await api.post<{ created: number; skipped: { name: string; reason: string }[] }>("/api/customers/import", {
        customers: csv!.customers.map((c) => c.customer),
      });
      const skipped = r.skipped.length ? ` Übersprungen: ${r.skipped.map((s) => `${s.name} (${s.reason})`).join(", ")}.` : "";
      return `${r.created} Kontakte importiert.${skipped}`;
    });

  return (
    <Sheet title="Kontakte übernehmen" onClose={result ? props.onDone : props.onClose}>
      <div className="cu-form">
        <div className="cu-seg wide" role="tablist">
          <button type="button" className={mode === "projekte" ? "on" : undefined} onClick={() => setMode("projekte")}>
            Aus Projekten
          </button>
          <button type="button" className={mode === "datei" ? "on" : undefined} onClick={() => setMode("datei")}>
            Aus einer Datei (CSV)
          </button>
        </div>

        {mode === "projekte" && (
          <div className="wide">
            <p className="muted-s">
              Diese Namen stehen im Feld „Kunde“ Ihrer Projekte. Aus jedem Namen wird ein Kunde, die passenden Projekte werden damit verknüpft. Das alte
              Feld bleibt, wie es ist.
            </p>
            {props.suggestions.length === 0 && <p className="cu-empty">Alle Projekte sind schon zugeordnet.</p>}
            <ul className="cu-pick">
              {props.suggestions.map((s) => (
                <li key={s.name}>
                  <label>
                    <input
                      type="checkbox"
                      checked={picked.has(s.name)}
                      onChange={(e) =>
                        setPicked((p) => {
                          const n = new Set(p);
                          if (e.target.checked) n.add(s.name);
                          else n.delete(s.name);
                          return n;
                        })
                      }
                    />
                    <span>{s.name}</span>
                    <small>{s.projectCount === 1 ? "1 Projekt" : `${s.projectCount} Projekte`}</small>
                  </label>
                </li>
              ))}
            </ul>
            <button className="dbtn" disabled={busy || picked.size === 0 || !!result} onClick={() => void fromProjects()}>
              {picked.size} Kunden anlegen
            </button>
          </div>
        )}

        {mode === "datei" && (
          <div className="wide">
            <p className="muted-s">
              Zum Beispiel der Kontakte-Export aus sevDesk oder Excel als CSV. Spalten wie Kundennummer, Organisation, Vorname, Nachname, Straße, PLZ, Ort,
              E-Mail, Telefon und Umsatzsteuer-ID werden erkannt. Vorhandene Kundennummern bleiben erhalten, die Nummerierung läuft dahinter weiter.
            </p>
            <input type="file" accept=".csv,text/csv,text/plain" onChange={(e) => void readFile(e.target.files?.[0])} />
            {csv && (
              <div className="cu-preview">
                <p>
                  <b>{fileName}</b>: {csv.customers.length} Kontakte erkannt
                  {csv.skipped.length > 0 && `, ${csv.skipped.length} ohne Namen übersprungen`}.
                </p>
                {csv.ignored.length > 0 && <p className="muted-s">Nicht übernommene Spalten: {csv.ignored.join(", ")}</p>}
                <ul>
                  {csv.customers.slice(0, 6).map((c) => (
                    <li key={c.line}>
                      {c.customer.number ? `${c.customer.number} · ` : ""}
                      {c.customer.name}
                      {c.customer.city ? `, ${c.customer.city}` : ""}
                    </li>
                  ))}
                  {csv.customers.length > 6 && <li>… und {csv.customers.length - 6} weitere</li>}
                </ul>
                <button className="dbtn" disabled={busy || csv.customers.length === 0 || !!result} onClick={() => void fromFile()}>
                  {csv.customers.length} Kontakte importieren
                </button>
              </div>
            )}
          </div>
        )}
        <Message>{error}</Message>
        <Message kind="ok">{result}</Message>
        {result && (
          <button className="dbtn ghost wide" onClick={props.onDone}>
            Fertig
          </button>
        )}
      </div>
    </Sheet>
  );
}

// ---------- Leistungen und Artikel ----------

/** „65,50“ oder „1.234,5“ → Cent; null, wenn es keine Zahl ist. */
export function eurosToCents(text: string): number | null {
  let s = text.replace(/[\s€]/g, "");
  if (!s) return null;
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  return Math.round(Number(s) * 100);
}

function ItemList({ manager }: { manager: boolean }) {
  const onAuth = useUnauthorizedRedirect();
  const [list, setList] = useState<ItemInfo[] | null>(null);
  const [archived, setArchived] = useState(false);
  const [error, setError] = useState("");
  const [edit, setEdit] = useState<ItemInfo | "new" | null>(null);

  const load = useCallback(() => {
    api
      .get<ItemInfo[]>(`/api/items${archived ? "?archived=1" : ""}`)
      .then(setList)
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [archived, onAuth]);
  useEffect(load, [load]);

  return (
    <section className="cu-panel">
      <div className="cu-bar">
        <p className="muted-s cu-lead">
          Ihre Standard-Leistungen und -Artikel. Beim Schreiben eines Angebots oder einer Rechnung übernehmen Sie sie mit einem Klick samt Preis und Steuersatz.
        </p>
        {manager && (
          <div className="cu-actions">
            <button className="dbtn" onClick={() => setEdit("new")}>
              <Icon name="plus" /> Leistung anlegen
            </button>
          </div>
        )}
      </div>
      <Message>{error}</Message>
      {list?.length === 0 && <p className="cu-empty">{archived ? "Nichts archiviert." : "Noch keine Leistungen. Zum Beispiel „Elektriker-Stunde“ oder „Anfahrt“."}</p>}
      {!!list?.length && (
        <table className="cu-table">
          <thead>
            <tr>
              <th>Bezeichnung</th>
              <th>Einheit</th>
              <th className="r">Preis netto</th>
              <th className="r">Steuer</th>
            </tr>
          </thead>
          <tbody>
            {list.map((i) => (
              <tr key={i.id} onClick={() => manager && setEdit(i)} tabIndex={manager ? 0 : -1} onKeyDown={(e) => manager && e.key === "Enter" && setEdit(i)}>
                <td>
                  <b>{i.name}</b>
                  {i.description && <small>{i.description}</small>}
                </td>
                <td>{i.unit}</td>
                <td className="r mono">{formatCents(i.priceCents)}</td>
                <td className="r">{i.vatRate} %</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="cu-foot">
        <button className="cu-link" onClick={() => setArchived((a) => !a)}>
          {archived ? "Zurück zu den aktiven Leistungen" : "Archivierte anzeigen"}
        </button>
      </p>
      {edit && (
        <ItemSheet
          item={edit === "new" ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            load();
          }}
        />
      )}
    </section>
  );
}

function ItemSheet(props: { item: ItemInfo | null; onClose: () => void; onSaved: () => void }) {
  const onAuth = useUnauthorizedRedirect();
  const { item } = props;
  const [f, setF] = useState({
    kind: item?.kind ?? "service",
    name: item?.name ?? "",
    description: item?.description ?? "",
    unit: item?.unit ?? "Std",
    price: item ? (item.priceCents / 100).toFixed(2).replace(".", ",") : "",
    vatRate: String(item?.vatRate ?? 19),
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError("");
    try {
      await fn();
      props.onSaved();
    } catch (err) {
      if (!onAuth(err)) setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const cents = eurosToCents(f.price);
    if (cents === null) {
      setError("Bitte geben Sie den Preis als Zahl an, zum Beispiel 65,50.");
      return;
    }
    const body = { kind: f.kind, name: f.name, description: f.description, unit: f.unit, priceCents: cents, vatRate: Number(f.vatRate) };
    void run(() => (item ? api.patch(`/api/items/${item.id}`, body) : api.post("/api/items", body)));
  }

  return (
    <Sheet title={item ? "Leistung bearbeiten" : "Leistung anlegen"} onClose={props.onClose}>
      <form className="cu-form" onSubmit={submit}>
        <div className="cu-seg wide" role="radiogroup" aria-label="Art">
          {(["service", "product"] as const).map((k) => (
            <button type="button" key={k} role="radio" aria-checked={f.kind === k} className={f.kind === k ? "on" : undefined} onClick={() => setF({ ...f, kind: k })}>
              {k === "service" ? "Leistung" : "Artikel"}
            </button>
          ))}
        </div>
        <label className="wide">
          Bezeichnung
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={160} required autoFocus />
        </label>
        <label className="wide">
          Beschreibung (optional)
          <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} maxLength={2000} />
        </label>
        <label>
          Einheit
          <input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} maxLength={12} placeholder="Std, Stk, m, pauschal" required />
        </label>
        <label>
          Preis netto in Euro
          <input value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} inputMode="decimal" placeholder="65,00" required />
        </label>
        <label>
          Steuersatz
          <select value={f.vatRate} onChange={(e) => setF({ ...f, vatRate: e.target.value })}>
            <option value="19">19 %</option>
            <option value="7">7 %</option>
            <option value="0">0 %</option>
          </select>
        </label>
        <Message>{error}</Message>
        <div className="cu-buttons wide">
          <button className="dbtn" disabled={busy}>
            Speichern
          </button>
          {item && (
            <>
              <button type="button" className="dbtn ghost" disabled={busy} onClick={() => void run(() => api.patch(`/api/items/${item.id}`, { archived: !item.archived }))}>
                {item.archived ? "Wiederherstellen" : "Archivieren"}
              </button>
              <button
                type="button"
                className="dbtn ghost danger"
                disabled={busy}
                onClick={() => window.confirm(`„${item.name}“ wirklich löschen?`) && void run(() => api.del(`/api/items/${item.id}`))}
              >
                Löschen
              </button>
            </>
          )}
        </div>
      </form>
    </Sheet>
  );
}

// ---------- Firmenangaben ----------

type BillingForm = Record<Exclude<keyof BillingSettings, "smallBusiness" | "paymentDays" | "nextCustomerNumber" | "pdfAccent" | "giroQr">, string> & {
  pdfAccent: string;
  giroQr: boolean;
  smallBusiness: boolean;
  paymentDays: string;
  nextCustomerNumber: string;
};

const textFields = [
  "legalName",
  "ownerName",
  "street",
  "postalCode",
  "city",
  "country",
  "phone",
  "email",
  "website",
  "taxNumber",
  "vatId",
  "registerInfo",
  "bankName",
  "iban",
  "bic",
] as const;

function toForm(s: BillingSettings): BillingForm {
  const out = { giroQr: s.giroQr, pdfAccent: s.pdfAccent ?? "", smallBusiness: s.smallBusiness, paymentDays: String(s.paymentDays), nextCustomerNumber: String(s.nextCustomerNumber) } as BillingForm;
  for (const k of textFields) out[k] = s[k] ?? "";
  return out;
}

export function BillingForm() {
  const onAuth = useUnauthorizedRedirect();
  const [f, setF] = useState<BillingForm | null>(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const branding = useBranding();
  const [editBrand, setEditBrand] = useState(false);

  useEffect(() => {
    api
      .get<BillingSettings>("/api/billing/settings")
      .then((s) => setF(toForm(s)))
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [onAuth]);

  const missing = useMemo(() => {
    if (!f) return [];
    const list: string[] = [];
    if (!f.legalName.trim()) list.push("Firmenname");
    if (!f.street.trim() || !f.postalCode.trim() || !f.city.trim()) list.push("Anschrift");
    if (!f.taxNumber.trim() && !f.vatId.trim()) list.push("Steuernummer oder Umsatzsteuer-ID");
    if (!f.iban.trim()) list.push("Bankverbindung (IBAN)");
    return list;
  }, [f]);

  if (!f) return <section className="cu-panel">{error ? <Message>{error}</Message> : <p className="muted-s">Lädt …</p>}</section>;
  const set = <K extends keyof BillingForm>(k: K, v: BillingForm[K]) => {
    setSaved(false);
    setF({ ...f, [k]: v });
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!f) return;
    setBusy(true);
    setError("");
    try {
      const body: Record<string, unknown> = {
        giroQr: f.giroQr,
        pdfAccent: f.pdfAccent || null,
        smallBusiness: f.smallBusiness,
        paymentDays: Number(f.paymentDays || 14),
        nextCustomerNumber: Number(f.nextCustomerNumber) || undefined,
      };
      for (const k of textFields) body[k] = f[k];
      const next = await api.put<BillingSettings>("/api/billing/settings", body);
      setF(toForm(next));
      setSaved(true);
    } catch (err) {
      if (!onAuth(err)) setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const field = (k: (typeof textFields)[number], label: string, extra: { wide?: boolean; placeholder?: string; maxLength?: number } = {}) => (
    <label className={extra.wide ? "wide" : undefined}>
      {label}
      <input value={f[k]} onChange={(e) => set(k, e.target.value)} placeholder={extra.placeholder} maxLength={extra.maxLength ?? 160} />
    </label>
  );

  return (
    <section className="cu-panel">
      <form className="cu-form cu-billing" onSubmit={submit}>
        <p className="muted-s wide">
          Diese Angaben stehen später auf jeder Rechnung und jedem Angebot. Rechnungen brauchen nach § 14 UStG Ihren Namen und Ihre Anschrift sowie
          Steuernummer oder Umsatzsteuer-ID. HandVero ist ein Werkzeug und ersetzt keine Steuerberatung. Bitte klären Sie vor dem Echtbetrieb alles mit Ihrer
          Steuerberatung.
        </p>
        <h3 className="wide">Ihr Betrieb</h3>
        {field("legalName", "Firmenname laut Rechnung", { wide: true })}
        {field("ownerName", "Inhaber oder Geschäftsführung")}
        {field("registerInfo", "Handelsregister (falls vorhanden)", { placeholder: "HRB 12345, Amtsgericht Chemnitz" })}
        {field("street", "Straße und Hausnummer", { wide: true })}
        {field("postalCode", "PLZ", { maxLength: 12 })}
        {field("city", "Ort", { maxLength: 80 })}
        {field("country", "Land", { maxLength: 2, placeholder: "DE" })}
        {field("phone", "Telefon", { maxLength: 40 })}
        {field("email", "E-Mail für Rechnungen", { maxLength: 254 })}
        {field("website", "Webseite", { maxLength: 200 })}

        <h3 className="wide">Steuer</h3>
        {field("taxNumber", "Steuernummer", { placeholder: "123/456/78901", maxLength: 20 })}
        {field("vatId", "Umsatzsteuer-ID", { placeholder: "DE123456789", maxLength: 20 })}
        <label className="wide cu-check">
          <input type="checkbox" checked={f.smallBusiness} onChange={(e) => set("smallBusiness", e.target.checked)} />
          <span>
            <b>Ich bin Kleinunternehmer (§ 19 UStG)</b>
            <small>Rechnungen werden dann ohne Umsatzsteuer geschrieben, mit dem vorgeschriebenen Hinweis. Die Grenzen: 25.000 € im Vorjahr und 100.000 € im laufenden Jahr.</small>
          </span>
        </label>

        <h3 className="wide">Bank und Zahlung</h3>
        {field("bankName", "Bank", { maxLength: 80 })}
        {field("iban", "IBAN", { placeholder: "DE89 3704 0044 0532 0130 00", maxLength: 40 })}
        {field("bic", "BIC", { maxLength: 11 })}
        <label>
          Zahlungsziel (Tage)
          <input value={f.paymentDays} onChange={(e) => set("paymentDays", e.target.value.replace(/\D/g, ""))} inputMode="numeric" maxLength={3} />
        </label>

        <h3 className="wide">Aussehen der PDFs</h3>
        <label>
          Farbe für Rechnungen, Angebote und Mahnungen
          <span className="cu-color">
            <input type="color" value={f.pdfAccent || "#1e3863"} onChange={(e) => set("pdfAccent", e.target.value)} aria-label="Farbe" />
            <button type="button" className="dbtn ghost small" onClick={() => set("pdfAccent", "")} disabled={!f.pdfAccent}>
              Standardblau
            </button>
          </span>
        </label>
        <label className="wide cu-check">
          <input type="checkbox" checked={f.giroQr} onChange={(e) => set("giroQr", e.target.checked)} />
          <span>
            <b>Überweisungs-QR-Code auf Rechnungen drucken</b>
            <small>Ihr Kunde scannt ihn mit der Banking-App, Empfänger, IBAN, Betrag und Rechnungsnummer sind dann schon ausgefüllt. Braucht Ihre IBAN.</small>
          </span>
        </label>
        <p className="muted-s">
          Die Farbe gilt für Überschriften, Linien und Summen. Farbe und Logo gelten nur für Ihren Betrieb. Die Farbe ändert nur das Aussehen, nie die Inhalte oder Beträge einer Rechnung.
        </p>
        <div className="wide">
          <BrandStrip branding={branding} onEdit={() => setEditBrand(true)} />
        </div>
        {editBrand && branding && <BrandingSheet branding={branding} onClose={() => setEditBrand(false)} />}

        <h3 className="wide">Nummern</h3>
        <label>
          Nächste Kundennummer
          <input value={f.nextCustomerNumber} onChange={(e) => set("nextCustomerNumber", e.target.value.replace(/\D/g, ""))} inputMode="numeric" maxLength={8} />
        </label>
        <p className="muted-s">
          Arbeiten Sie bisher mit einem anderen Programm, tragen Sie hier die nächste freie Nummer ein, damit die Nummern ohne Lücke weiterlaufen. Nach unten
          lässt sich der Zähler nicht stellen, damit keine Nummer doppelt vergeben wird.
        </p>

        {missing.length > 0 && <Message kind="info">Für Rechnungen fehlen noch: {missing.join(", ")}.</Message>}
        <Message>{error}</Message>
        <Message kind="ok">{saved ? "Gespeichert." : ""}</Message>
        <div className="cu-buttons wide">
          <button className="dbtn" disabled={busy}>
            Speichern
          </button>
        </div>
      </form>
    </section>
  );
}
